"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { createSharedContext } from "@/lib/utils";
import { getMessage } from "./get-message";
import type { MessageTree } from "./types";
import { DEFAULT_LOCALE, LEGACY_LOCALE_STORAGE_KEY, LOCALE_STORAGE_KEY, type Locale } from "./types";

const messageCache: Partial<Record<Locale, MessageTree>> = {};

async function loadLocaleMessages(locale: Locale): Promise<MessageTree> {
  const cached = messageCache[locale];
  if (cached !== undefined) return cached;
  if (locale === "vi") {
    const mod = await import("./messages/vi");
    messageCache.vi = mod.vi;
    return mod.vi;
  }
  const mod = await import("./messages/en");
  messageCache.en = mod.en;
  return mod.en;
}

export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
}

/**
 * HMR / lazy chunks can load this module twice. Duplicate `createContext` makes
 * Provider and consumers see different contexts → null → throw.
 * Pin both React context and locale listeners on globalThis.
 */
const I18N_LOCALE_STORE_KEY = "__9s_i18n_locale_store__";

type LocaleStore = {
  listeners: Set<() => void>;
  emit: () => void;
  subscribe: (onStoreChange: () => void) => () => void;
};

function getLocaleStore(): LocaleStore {
  const g = globalThis as typeof globalThis & { [I18N_LOCALE_STORE_KEY]?: LocaleStore };
  const existing = g[I18N_LOCALE_STORE_KEY];
  if (existing) return existing;

  const store: LocaleStore = {
    listeners: new Set(),
    emit() {
      store.listeners.forEach((l) => l());
    },
    subscribe(onStoreChange) {
      store.listeners.add(onStoreChange);
      const onStorage = (e: StorageEvent) => {
        if (e.key === LOCALE_STORAGE_KEY || e.key === null) onStoreChange();
      };
      if (typeof window !== "undefined") {
        window.addEventListener("storage", onStorage);
      }
      return () => {
        store.listeners.delete(onStoreChange);
        if (typeof window !== "undefined") {
          window.removeEventListener("storage", onStorage);
        }
      };
    },
  };
  g[I18N_LOCALE_STORE_KEY] = store;
  return store;
}

const I18nContext = createSharedContext<I18nContextValue | null>("__9s_i18n_react_context__", null);

export function translateSnapshot(
  key: string,
  values?: Record<string, string | number>,
): string {
  return getMessage(messageCache[readLocaleSnapshot()], key, values);
}

export function localeDateTag(): "vi-VN" | "en-GB" {
  return readLocaleSnapshot() === "vi" ? "vi-VN" : "en-GB";
}

export function readLocaleSnapshot(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "en" || stored === "vi") return stored;
    const legacy = localStorage.getItem(LEGACY_LOCALE_STORAGE_KEY);
    if (legacy === "en" || legacy === "vi") {
      localStorage.setItem(LOCALE_STORAGE_KEY, legacy);
      return legacy;
    }
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE;
}

function getServerLocaleSnapshot(): Locale {
  return DEFAULT_LOCALE;
}

function buildI18nValue(
  locale: Locale,
  setLocale: (locale: Locale) => void,
  messages: MessageTree | undefined,
): I18nContextValue {
  return {
    locale,
    setLocale,
    t: (key: string, values?: Record<string, string | number>) => getMessage(messages, key, values),
  };
}

export interface I18nProviderProps {
  children: React.ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const localeStore = getLocaleStore();
  const locale = useSyncExternalStore(localeStore.subscribe, readLocaleSnapshot, getServerLocaleSnapshot);
  const [messages, setMessages] = useState<MessageTree | undefined>(() => messageCache[locale]);
  const [activeLocale, setActiveLocale] = useState<Locale>(locale);

  useLayoutEffect(() => {
    try {
      document.documentElement.lang = locale;
    } catch {
      // ignore
    }
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    const cached = messageCache[locale];
    if (cached !== undefined) {
      setMessages(cached);
      setActiveLocale(locale);
      return;
    }
    void loadLocaleMessages(locale).then((loaded) => {
      if (cancelled) return;
      setMessages(loaded);
      setActiveLocale(locale);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch {
      // ignore
    }
    getLocaleStore().emit();
  }, []);

  const t = useCallback(
    (key: string, values?: Record<string, string | number>) => getMessage(messages, key, values),
    [messages],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale: activeLocale, setLocale, t }),
    [activeLocale, setLocale, t],
  );

  if (messages === undefined) {
    return null;
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Prefer Provider value; if context is missing (legacy HMR / stray consumer),
 * still resolve from the shared locale store so the shell does not hard-crash.
 */
export function useI18nContext(): I18nContextValue {
  const ctx = useContext(I18nContext);
  const localeStore = getLocaleStore();
  const locale = useSyncExternalStore(localeStore.subscribe, readLocaleSnapshot, getServerLocaleSnapshot);
  const [messages, setMessages] = useState<MessageTree | undefined>(() => messageCache[locale]);

  useEffect(() => {
    let cancelled = false;
    const cached = messageCache[locale];
    if (cached !== undefined) {
      setMessages(cached);
      return;
    }
    void loadLocaleMessages(locale).then((loaded) => {
      if (cancelled) return;
      setMessages(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch {
      // ignore
    }
    getLocaleStore().emit();
  }, []);

  const storeValue = useMemo(() => buildI18nValue(locale, setLocale, messages), [locale, setLocale, messages]);

  if (ctx !== null) return ctx;
  return storeValue;
}
